import { graphql, navigate } from "gatsby";
import orderBy from "lodash/orderBy";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../components/Layout";
import ProductSEO from "../components/product/SEO";
import Hero from "../components/productListing/Hero";
import ProductFilterWrapper from "../components/productListing/ProductFilterWrapper";
import { setFilter, setSortBy } from "../slices/filter";
import { VendorsFilter } from "../utils/filter";
import { useDatalayer } from "../utils/useDatalayer";
import { RunFilter, buildQuery } from "./QueryBuilder";

const SITE_URL = process.env.GATSBY_PRIMARY_URL;
const ACTIVE_LOCALE = process.env.GATSBY_LOCALE_CURRENCY;
const COUNTRY_CODE = process.env.GATSBY_LOCALE_COUNTRY;
const ProductCollection = ({ data, location }) => {
  const collection = data.shopifyCollection;
  const collectionTitle = data.contentfulCollections?.title || collection.title;
  const collectionDescription =
    data.contentfulCollections?.description?.description;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { sortBy, filter } = useSelector((state) => state.filter);
  const params = new URLSearchParams(location?.search);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortedProducts, setSortedProducts] = useState([]);
  function handleClick(filterArr, currentValue, setValue) {
    const response = VendorsFilter(filterArr, currentValue);
    setValue(response);
  }

  function createColourVariants(products) {
    return products.flatMap((product) => {
      const seenColors = new Set();
      const colorVariants = product.variants
          .filter((variant) =>
              variant.selectedOptions.some((opt) =>
                  ['color', 'colour'].includes(opt.name.toLowerCase())
              )
          )
          .filter((variant) => {
            const hidden = variant[`hidden_${COUNTRY_CODE.toLowerCase()}`]?.value === 'true';
            const hideFromCollectionList = variant.hideFromCollectionList?.value === 'true';
            if (hidden || hideFromCollectionList) {
              return;
            }

            const colorOption = variant.selectedOptions.find((opt) =>
                ['color', 'colour'].includes(opt.name.toLowerCase())
            )?.value;

            if (seenColors.has(colorOption)) {
              return false;
            }

            seenColors.add(colorOption);
            return true;
          })
          .map((variant) => {
            const colorOption = variant.selectedOptions.find((opt) =>
                ['color', 'colour'].includes(opt.name.toLowerCase())
            )?.value;

            return {
              ...product,
              id: `${product.id}-${variant.shopifyId}`,
              title: `${product.title} - ${colorOption}`,
              handle: `${product.handle}?variant=${variant.shopifyId.replace('gid://shopify/ProductVariant/', '')}`, // URL with variant pre-selected
              media: variant.media,
            };
          });
      if (colorVariants.length > 1) {
        return colorVariants;
      }

      return product;
    });
  }

  const { viewCollection } = useDatalayer();

  useEffect(() => {
    viewCollection(collection);
  }, []);

  useEffect(() => {
    const filter = {
      availability: params?.getAll("availability") || [],
      sizes: params?.getAll("sizes") || [],
      price: {
        min: params?.get("price_min") || 0,
        max: params?.get("price_max") || 0,
      },
      colors: params?.getAll("colors") || [],
      vendors: params?.getAll("vendors") || [],
    };
    dispatch(setFilter(filter));
    dispatch(setSortBy(params?.get("sortBy") || "featured"));
    setLoading(true);
  }, []);

  useEffect(() => {
    if (loading) {
      const url = buildQuery(filter, sortBy);
      navigate(url);
      const productsWithVariants = collection.products.filter((product) => {
        return product.variants.length > 0;
      });
      const productsWithColourVariants = createColourVariants(productsWithVariants);
      const filterProducts = RunFilter(productsWithColourVariants, filter);
      setFilteredProducts(filterProducts);
    }
  }, [filter, loading]);

  useEffect(() => {
    params.append("sortBy", sortBy);
    if (sortBy === "featured") {
      setSortedProducts(filteredProducts);
    }
    // if (sortBy === "asc") {
    //   setSortedProducts(orderBy(filteredProducts, ["title"], ["asc"]));
    // }
    // if (sortBy === "desc") {
    //   setSortedProducts(orderBy(filteredProducts, ["title"], ["desc"]));
    // }
    if (sortBy === "price_h") {
      setSortedProducts(
        orderBy(
          filteredProducts,
          (product) => product.priceRangeV2.minVariantPrice.amount / 100,
          ["asc"]
        )
      );
    }
    if (sortBy === "price_l") {
      setSortedProducts(
        orderBy(
          filteredProducts,
          (product) => product.priceRangeV2.minVariantPrice.amount / 100,
          ["desc"]
        )
      );
    }
    // if (sortBy === "date_asc") {
    //   setSortedProducts(orderBy(filteredProducts, ["createdAt"], ["asc"]));
    // }
    // if (sortBy === "date_desc") {
    //   setSortedProducts(orderBy(filteredProducts, ["createdAt"], ["desc"]));
    // }
  }, [sortBy, filteredProducts]);

  return (
    <Layout>
      <div className="relative block w-full box-border">
        <div className="w-full px-5 2xl:px-[90px] border-inherit">
          <Hero title={collectionTitle} description={collectionDescription} />
          <ProductFilterWrapper
            products={collection.products}
            filteredProducts={sortedProducts}
            handleClick={handleClick}
          />
        </div>
      </div>
    </Layout>
  );
};

export const query = graphql`
  query ProductCollection($handle: String!) {
    contentfulCollections(handle: { eq: $handle }) {
      title
      seoTitle
      seoDescription {
        seoDescription
      }
      description {
        description
      }
    }
    shopifyCollection(handle: { eq: $handle }) {
      title
      handle
      description
      products {
        ...ProductFragment
      }
    }
  }
`;

export function Head({ location, data }) {
  const now = new Date();
  const oneMonthFromNow = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const title = data.contentfulCollections
    ? data.contentfulCollections?.seoTitle || data.contentfulCollections?.title
    : data.shopifyCollection.title;
  const description = data.contentfulCollections
    ? data.contentfulCollections.seoDescription?.seoDescription
    : data.shopifyCollection.description;

  const productsSchema = data.shopifyCollection.products.map((product) => {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.title,
      brand: { "@type": "Brand", name: product.vendor },
      sku: product.variants[0]?.sku,
      mpn: product.variants[0]?.barcode,
      description: product?.description,
      url: `${SITE_URL}/products/${product.handle}`,
      image: product.media[0]?.preview?.image?.originalSrc,
      itemCondition: "https://schema.org/NewCondition",
      offers: product.variants.map((variant) => {
        const variantId = variant.shopifyId.replace(
          "gid://shopify/ProductVariant/",
          ""
        );
        return {
          "@type": "Offer",
          price: variant.price,
          priceCurrency: ACTIVE_LOCALE,
          itemCondition: "https://schema.org/NewCondition",
          url: `${SITE_URL}/products/${product.handle}?variant=${variantId}`,
          sku: variant.sku,
          mpn: variant.barcode,
          availability: variant.availableForSale
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          priceValidUntil: oneMonthFromNow.toISOString().split("T")[0],
          gtin14: variant.barcode,
        };
      }),
    };
  });

  const jsonSchema = [
    {
      "@context": "http://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: description,
      url: `${SITE_URL}/collections/${data.shopifyCollection.handle}`,
      inLanguage: "en",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: productsSchema,
      },
    },
  ];
  return (
    <ProductSEO
      location={location}
      data={{
        title,
        description,
        jsonSchema,
      }}
    />
  );
}

export default ProductCollection;
